import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import _ from "lodash";
import { NAME_FILTERS_LOCAL_STORAGE, NAME_QUERY_PARAMS } from "../pages/Activities/All/constants";

export function useActivitiesFilters() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const defaultDateRange = [moment().subtract(3, 'months'), moment()];
  const defaultDevices = ['web', 'app'];

  // Filtros
  const [selectedDay, setSelectedDay] = useState(() => {
    // 1. Prioridade, sempre será parametros da URL
    const savedValueURL = urlParams.get(NAME_QUERY_PARAMS.DAY);
    const savedValue = localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.DAY);
    try {
      // Primeiro, tenta buscar pela URL 
      if (!savedValueURL) {

        // Segundo, pela localStorage
        if (!savedValue) return defaultDateRange;
        const parsed = JSON.parse(savedValue);
        if (parsed && parsed[0] && parsed[1]) return [moment(parsed[0]), moment(parsed[1])];
      }

      const parsedURL = JSON.parse(savedValueURL);
      if (parsedURL && parsedURL[0] && parsedURL[1]) return [moment(parsedURL[0]), moment(parsedURL[1])];
    } catch (ex) {
      console.error("Não foi possível verificar os filtros persistentes para data.", ex);
    }
    return defaultDateRange;
  });
  const [selectedType, setSelectedType] = useState(() => urlParams.get(NAME_QUERY_PARAMS.TYPE) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.TYPE) || null);
  const [selectedPage, setSelectedPage] = useState(() => urlParams.get(NAME_QUERY_PARAMS.PAGE) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.PAGE) || null);
  const [selectedForm, setSelectedForm] = useState(() => urlParams.get(NAME_QUERY_PARAMS.FORM) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.FORM) || null);
  const [selectedAuthor, setSelectedAuthor] = useState(() => urlParams.get(NAME_QUERY_PARAMS.AUTHOR) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.AUTHOR) || null);
  const [selectedDevice, setSelectedDevice] = useState(() => {
    const val = urlParams.get(NAME_QUERY_PARAMS.DEVICE) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.DEVICE);
    if (val) {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {
        return val.split(',');
      }
    }
    return defaultDevices;
  });
  const [selectedNetwork, setSelectedNetwork] = useState(() => urlParams.get(NAME_QUERY_PARAMS.NETWORK) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.NETWORK) || null);
  const [selectedIdsAffected, setSelectedIdsAffected] = useState(() => urlParams.get(NAME_QUERY_PARAMS.IDS_AFFECTED) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.IDS_AFFECTED) || null);

  // 1. Sync state to URL/LocalStorage when state changes
  useEffect(() => {
    // Avoid updating if the ID is inadvertently set to the string "[object Object]"
    if (selectedIdsAffected === "[object Object]") {
      setSelectedIdsAffected(null);
      return;
    }

    const newParams = new URLSearchParams(location.search);

    // Update params based on state
    if (selectedAuthor) {
      newParams.set(NAME_QUERY_PARAMS.AUTHOR, selectedAuthor);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.AUTHOR, selectedAuthor);
    } else {
      newParams.delete(NAME_QUERY_PARAMS.AUTHOR);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.AUTHOR);
    }

    if (selectedDevice && selectedDevice.length > 0) {
      const deviceStr = selectedDevice.join(',');
      newParams.set(NAME_QUERY_PARAMS.DEVICE, deviceStr);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.DEVICE, deviceStr);
    } else {
      newParams.delete(NAME_QUERY_PARAMS.DEVICE);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.DEVICE);
    }

    if (selectedDay) {
      newParams.set(NAME_QUERY_PARAMS.DAY, JSON.stringify([selectedDay[0].toISOString(), selectedDay[1].toISOString()]));
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.DAY, JSON.stringify([selectedDay[0].toISOString(), selectedDay[1].toISOString()]));
    } else {
      newParams.delete(NAME_QUERY_PARAMS.DAY);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.DAY);
    }

    if (selectedForm) {
      newParams.set(NAME_QUERY_PARAMS.FORM, selectedForm);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.FORM, selectedForm);
    } else {
      newParams.delete(NAME_QUERY_PARAMS.FORM);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.FORM);
    }

    if (selectedNetwork) {
      newParams.set(NAME_QUERY_PARAMS.NETWORK, selectedNetwork);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.NETWORK, selectedNetwork);
    } else {
      newParams.delete(NAME_QUERY_PARAMS.NETWORK);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.NETWORK);
    }

    if (selectedPage) {
      newParams.set(NAME_QUERY_PARAMS.PAGE, selectedPage);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.PAGE, selectedPage);
    } else {
      newParams.delete(NAME_QUERY_PARAMS.PAGE);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.PAGE);
    }

    if (selectedType) {
      newParams.set(NAME_QUERY_PARAMS.TYPE, selectedType);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.TYPE, selectedType);
    } else {
      newParams.delete(NAME_QUERY_PARAMS.TYPE);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.TYPE);
    }

    if (selectedIdsAffected) {
      newParams.set(NAME_QUERY_PARAMS.IDS_AFFECTED, selectedIdsAffected);
      localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.IDS_AFFECTED, selectedIdsAffected);
    } else {
      newParams.delete(NAME_QUERY_PARAMS.IDS_AFFECTED);
      localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.IDS_AFFECTED);
    }

    const newSearch = newParams.toString();
    if (location.search.substring(1) !== newSearch) {
      navigate({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      }, { replace: true });
    }
  }, [
    selectedAuthor,
    selectedDevice,
    selectedDay,
    selectedForm,
    selectedNetwork,
    selectedPage,
    selectedType,
    selectedIdsAffected,
    navigate,
    location.pathname
  ]);

  // 2. Sync FROM URL (for external navigation, back/forward button)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get(NAME_QUERY_PARAMS.IDS_AFFECTED);

    if (idFromUrl && idFromUrl !== selectedIdsAffected && idFromUrl !== "[object Object]") {
      setSelectedIdsAffected(idFromUrl);
    } else if (!idFromUrl && selectedIdsAffected) {
      // Logic for clearing when URL param is removed could go here
    }
  }, [location.search]);

  /**
   * Limpando campos de filtro
   */
  function handleClear() {
    setSelectedDay(defaultDateRange);
    setSelectedType(null);
    setSelectedPage(null);
    setSelectedForm(null);
    setSelectedAuthor(null);
    setSelectedDevice(defaultDevices);
    setSelectedNetwork(null);
    setSelectedIdsAffected(null);
  }

  // Um filtro só conta como "ativo" quando difere do padrão. O período e o
  // dispositivo têm valores padrão, então só contam quando o usuário os altera.
  const isDefaultDevice =
    Array.isArray(selectedDevice) &&
    selectedDevice.length === defaultDevices.length &&
    defaultDevices.every((d) => selectedDevice.includes(d));

  const isDefaultDay =
    !selectedDay ||
    (selectedDay[0].isSame(moment().subtract(3, "months"), "day") &&
      selectedDay[1].isSame(moment(), "day"));

  const activeFilterCount = [
    selectedType,
    selectedPage,
    selectedForm,
    selectedAuthor,
    selectedNetwork,
    selectedIdsAffected,
    isDefaultDevice ? null : selectedDevice,
    isDefaultDay ? null : selectedDay,
  ].filter(Boolean).length;

  const hasAnyFilter = activeFilterCount > 0;

  const filters = useMemo(() => {
    const filterParams = {
      startDate: selectedDay ? selectedDay[0].startOf('day').toISOString() : null,
      endDate: selectedDay ? selectedDay[1].endOf('day').toISOString() : null,
      action: selectedType,
      page: selectedPage,
      form: selectedForm,
      userId: selectedAuthor,
      device: selectedDevice && selectedDevice.length > 0 ? selectedDevice.join(',') : null,
      network: selectedNetwork,
      ids: selectedIdsAffected // Mapping idsAffected URL param to ids API param
    };

    // Remove null/undefined values to avoid sending them to API
    const cleanParams = Object.fromEntries(
      Object.entries(filterParams).filter(([_, value]) => value != null && value !== '')
    );

    return cleanParams;
  }, [
    selectedDay,
    selectedType,
    selectedPage,
    selectedForm,
    selectedAuthor,
    selectedDevice,
    selectedNetwork,
    selectedIdsAffected
  ]);

  return {
    selectedDay, setSelectedDay,
    selectedType, setSelectedType,
    selectedPage, setSelectedPage,
    selectedForm, setSelectedForm,
    selectedAuthor, setSelectedAuthor,
    selectedDevice, setSelectedDevice,
    selectedNetwork, setSelectedNetwork,
    selectedIdsAffected, setSelectedIdsAffected,
    handleClear,
    hasAnyFilter,
    activeFilterCount,
    filters // Expose formatted filters for API usage
  };
}
